import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UploadedDataInfo {
  fileName: string;
  fileType: 'json' | 'csv';
  data: any[];
  columns: string[];
  rowCount: number;
  uploadedAt: Date;
  originalFile?: File; // Store the original file for datasource creation
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private uploadedDataSubject = new BehaviorSubject<UploadedDataInfo | null>(null);
  public uploadedData$ = this.uploadedDataSubject.asObservable();

  constructor() { }

  /**
   * Open file dialog and handle file selection
   * @param acceptedTypes Array of accepted file types (e.g., ['.json', '.csv'])
   * @returns Promise<UploadedDataInfo | null>
   */
  openFileDialog(acceptedTypes: string[] = ['.json', '.csv']): Promise<UploadedDataInfo | null> {
    return new Promise((resolve, reject) => {
      // Create hidden file input element
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = acceptedTypes.join(',');
      fileInput.style.display = 'none';

      // Handle file selection
      fileInput.addEventListener('change', async (event: any) => {
        const file: File = event.target.files?.[0];
        if (!file) {
          resolve(null);
          return;
        }

        try {
          const uploadedData = await this.processFile(file);
          this.setUploadedData(uploadedData);
          resolve(uploadedData);
        } catch (error) {
          reject(error);
        } finally {
          // Clean up the input element
          document.body.removeChild(fileInput);
        }
      });

      // Handle cancel (when user closes dialog without selecting)
      fileInput.addEventListener('cancel', () => {
        document.body.removeChild(fileInput);
        resolve(null);
      });

      // Add to DOM and trigger click
      document.body.appendChild(fileInput);
      fileInput.click();
    });
  }

  /**
   * Process uploaded file and extract data
   * @param file File object to process
   * @returns Promise<UploadedDataInfo>
   */
  async processFile(file: File): Promise<UploadedDataInfo> {
    const fileName = file.name;
    const fileExtension = fileName.toLowerCase().split('.').pop();

    if (!fileExtension || !['json', 'csv'].includes(fileExtension)) {
      throw new Error('Unsupported file type. Please select a JSON or CSV file.');
    }

    const fileContent = await this.readFileContent(file);

    let data: any[];
    let columns: string[];

    if (fileExtension === 'json') {
      data = this.parseJsonFile(fileContent);
      columns = this.extractColumnsFromJson(data);
    } else {
      const csvResult = this.parseCsvFile(fileContent);
      data = csvResult.data;
      columns = csvResult.columns;
    }

    const uploadedDataInfo: UploadedDataInfo = {
      fileName,
      fileType: fileExtension as 'json' | 'csv',
      data,
      columns,
      rowCount: data.length,
      uploadedAt: new Date(),
      originalFile: file // Store the original file
    };

    return uploadedDataInfo;
  }

  /**
   * Read file content as text
   * @param file File to read
   * @returns Promise<string>
   */
  private readFileContent(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Parse JSON file content
   * @param content File content as string
   * @returns Array of objects
   */
  private parseJsonFile(content: string): any[] {
    try {
      const parsed = JSON.parse(content);
      
      // Handle different JSON structures
      if (Array.isArray(parsed)) {
        return parsed;
      } else if (parsed && typeof parsed === 'object') {
        // If it's a single object, wrap it in an array
        return [parsed];
      } else {
        throw new Error('JSON file must contain an array of objects or a single object');
      }
    } catch (error) {
      throw new Error('Invalid JSON file format');
    }
  }

  /**
   * Parse CSV file content
   * @param content CSV file content as string
   * @returns Object with data array and columns array
   */
  private parseCsvFile(content: string): { data: any[], columns: string[] } {
    // Parse CSV respecting quoted fields with newlines
    const rows = this.parseCsvRows(content.trim());
    
    if (rows.length === 0) {
      throw new Error('CSV file is empty');
    }

    // Extract headers from first row
    const headers = rows[0];
    
    if (headers.length === 0) {
      throw new Error('CSV file has no columns');
    }

    // Parse data rows
    const data: any[] = [];
    for (let i = 1; i < rows.length; i++) {
      const values = rows[i];
      const row: any = {};
      
      // Map values to headers, handling cases where row has different number of columns
      headers.forEach((header, index) => {
        row[header] = index < values.length ? this.convertValue(values[index]) : null;
      });
      
      data.push(row);
    }

    return {
      data,
      columns: headers
    };
  }

  /**
   * Parse CSV content into rows, properly handling quoted fields with newlines
   * @param content CSV file content
   * @returns Array of rows, where each row is an array of values
   */
  private parseCsvRows(content: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let i = 0; i < content.length; i++) {
      const char = content[i];
      const nextChar = i < content.length - 1 ? content[i + 1] : null;
      
      if (char === '"') {
        // Handle escaped quotes (two consecutive quotes)
        if (inQuotes && nextChar === '"') {
          currentField += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        currentRow.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        // Row separator (handle both \n and \r\n)
        if (char === '\r' && nextChar === '\n') {
          i++; // Skip the \n in \r\n
        }
        
        // Add current field and row if not empty
        if (currentField || currentRow.length > 0) {
          currentRow.push(currentField.trim());
          rows.push(currentRow);
          currentRow = [];
          currentField = '';
        }
      } else {
        // Regular character
        currentField += char;
      }
    }
    
    // Add the last field and row if not empty
    if (currentField || currentRow.length > 0) {
      currentRow.push(currentField.trim());
      rows.push(currentRow);
    }
    
    return rows;
  }

  /**
   * Convert string value to appropriate type (number, boolean, or string)
   * @param value String value to convert
   * @returns Converted value
   */
  private convertValue(value: string): any {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const trimmedValue = value.trim();

    // Check for boolean values
    if (trimmedValue.toLowerCase() === 'true') return true;
    if (trimmedValue.toLowerCase() === 'false') return false;

    // Check for number values
    if (!isNaN(Number(trimmedValue)) && trimmedValue !== '') {
      return Number(trimmedValue);
    }

    // Return as string
    return trimmedValue;
  }

  /**
   * Extract column names from JSON data
   * @param data Array of objects
   * @returns Array of column names
   */
  private extractColumnsFromJson(data: any[]): string[] {
    if (data.length === 0) {
      return [];
    }

    // Get all unique keys from all objects
    const allKeys = new Set<string>();
    data.forEach(item => {
      if (item && typeof item === 'object') {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });

    return Array.from(allKeys);
  }

  /**
   * Set the uploaded data (also triggers subject update)
   * @param data UploadedDataInfo to store
   */
  setUploadedData(data: UploadedDataInfo | null): void {
    this.uploadedDataSubject.next(data);
  }

  /**
   * Get current uploaded data
   * @returns UploadedDataInfo or null
   */
  getUploadedData(): UploadedDataInfo | null {
    return this.uploadedDataSubject.value;
  }

  /**
   * Clear uploaded data
   */
  clearUploadedData(): void {
    this.uploadedDataSubject.next(null);
  }

  /**
   * Convert a file to base64 string
   * @param file File to convert
   * @returns Promise<string> - Base64-encoded file content
   */
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          // Remove the data URL prefix (e.g., "data:application/json;base64,")
          const base64String = (reader.result as string).split(',')[1];
          resolve(base64String);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file for base64 conversion'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Open file dialog and return file object with base64 content
   * Useful for uploading files to the API
   * @param acceptedTypes Array of accepted file types
   * @returns Promise with file details including base64 content
   */
  async selectFileForUpload(acceptedTypes: string[] = ['.json', '.csv']): Promise<{
    file: File;
    fileName: string;
    fileContent: string;
  } | null> {
    return new Promise((resolve, reject) => {
      // Create hidden file input element
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = acceptedTypes.join(',');
      fileInput.style.display = 'none';

      // Handle file selection
      fileInput.addEventListener('change', async (event: any) => {
        const file: File = event.target.files?.[0];
        if (!file) {
          resolve(null);
          return;
        }

        try {
          const fileContent = await this.fileToBase64(file);
          resolve({
            file,
            fileName: file.name,
            fileContent
          });
        } catch (error) {
          reject(error);
        } finally {
          // Clean up the input element
          document.body.removeChild(fileInput);
        }
      });

      // Handle cancel (when user closes dialog without selecting)
      fileInput.addEventListener('cancel', () => {
        document.body.removeChild(fileInput);
        resolve(null);
      });

      // Add to DOM and trigger click
      document.body.appendChild(fileInput);
      fileInput.click();
    });
  }
}
